import { AlertTriangle, ArrowLeft, CloudUpload, Fingerprint, KeyRound } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSession } from "../App.js";
import { api, type VaultMode } from "../lib/api.js";
import { passkeyHostnameProblem, passkeysSupported, registerPasskey } from "../lib/passkeys.js";
import { ModePicker } from "../lib/vaultModes.js";

type StorageForm = {
  kind: "r2" | "s3" | "fs";
  cloudflareApiToken: string;
  r2BucketName: string;
  bucket: string;
  region: string;
  endpoint: string;
  forcePathStyle: boolean;
  accessKeyId: string;
  secretAccessKey: string;
  prefix: string;
  dir: string;
  localAck: boolean;
};

function toConfig(f: StorageForm) {
  if (f.kind === "fs") return { kind: "fs", dir: f.dir };
  return {
    kind: "s3",
    bucket: f.bucket,
    ...(f.region ? { region: f.region } : {}),
    ...(f.endpoint ? { endpoint: f.endpoint } : {}),
    ...(f.forcePathStyle ? { forcePathStyle: true } : {}),
    ...(f.accessKeyId ? { accessKeyId: f.accessKeyId } : {}),
    ...(f.secretAccessKey ? { secretAccessKey: f.secretAccessKey } : {}),
    ...(f.prefix ? { prefix: f.prefix } : {}),
  };
}

export function Setup() {
  const { status, refresh } = useSession();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const modeParam = params.get("mode") as VaultMode | null;
  const mode: VaultMode | null =
    modeParam === "kid" || modeParam === "guardian" || modeParam === "circle" ? modeParam : null;

  const [setupToken, setSetupToken] = useState("");
  const [form, setForm] = useState<StorageForm>({
    kind: "r2",
    cloudflareApiToken: "",
    r2BucketName: "",
    bucket: "",
    region: "us-east-1",
    endpoint: "",
    forcePathStyle: false,
    accessKeyId: "",
    secretAccessKey: "",
    prefix: "",
    dir: ".data/bucket",
    localAck: false,
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ownerName, setOwnerName] = useState("");
  const [storageFromEnv, setStorageFromEnv] = useState(false);

  useEffect(() => {
    api<{ storageFromEnv: boolean }>("/v1/setup/status")
      .then((s) => setStorageFromEnv(s.storageFromEnv))
      .catch(() => undefined);
  }, []);

  const storageDone = !!status?.setup.storageConfigured;
  const ownerDone = !!status?.setup.hasOwner;
  const step = !mode ? 1 : !storageDone ? 2 : !ownerDone ? 3 : 4;

  useEffect(() => {
    if (step === 4 && mode) nav(`/vaults/new?mode=${mode}`, { replace: true });
  }, [step, mode, nav]);

  const set = (k: keyof StorageForm, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  async function test() {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const r = await api<{ health: { ok: boolean; detail?: string; latencyMs: number } }>(
        "/v1/setup/storage/test",
        {
          body: { setupToken, config: toConfig(form) },
        },
      );
      setMsg(
        r.health.ok
          ? `Connected (${r.health.latencyMs} ms).`
          : `Not reachable: ${r.health.detail ?? "unknown"}`,
      );
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function save() {
    setBusy(true);
    setErr(null);
    try {
      await api("/v1/setup/storage", { body: { setupToken, config: toConfig(form) } });
      await refresh();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function provisionR2() {
    setBusy(true);
    setErr(null);
    try {
      await api("/v1/setup/storage/r2/provision", {
        body: {
          setupToken,
          cloudflareApiToken: form.cloudflareApiToken,
          ...(form.r2BucketName ? { bucketName: form.r2BucketName } : {}),
        },
      });
      await refresh();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function createOwner() {
    setBusy(true);
    setErr(null);
    try {
      await registerPasskey({ name: ownerName, setupToken });
      await refresh();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const canSaveLocal = form.kind !== "fs" || form.localAck;

  if (step === 1) {
    return <ModePicker onPick={(m) => setParams({ mode: m }, { replace: true })} />;
  }

  return (
    <div>
      <div className="steps">
        <div className="done">1. Mode</div>
        <div className={step === 2 ? "active" : "done"}>2. Storage</div>
        <div className={step === 3 ? "active" : step > 3 ? "done" : ""}>3. Your passkey</div>
        <div className={step === 4 ? "active" : ""}>4. Create your first vault</div>
      </div>

      <div className="card">
        <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <KeyRound size={18} className="icon" />
          Setup token
        </h2>
        <p className="muted">
          Printed by the gateway at startup (or set as <code>TETHER_ADMIN_TOKEN</code>). It proves
          you are the person who deployed this gateway.
        </p>
        <input
          value={setupToken}
          onChange={(e) => setSetupToken(e.target.value)}
          placeholder="setup token"
          autoComplete="off"
        />
      </div>

      {step === 2 && (
        <div className="card">
          <div className="row" style={{ justifyContent: "space-between" }}>
            <h2 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
              <CloudUpload size={18} className="icon" />
              Where should your data live?
            </h2>
            <button
              onClick={() => setParams({}, { replace: true })}
              className="muted"
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <ArrowLeft size={14} />
              Change mode
            </button>
          </div>
          {storageFromEnv ? (
            <p className="muted">Storage is configured by the environment of this gateway.</p>
          ) : (
            <>
              <label>Provider</label>
              <select value={form.kind} onChange={(e) => set("kind", e.target.value)}>
                <option value="r2">Cloudflare R2 (recommended — cheap, no download fees)</option>
                <option value="s3">S3-compatible (advanced: AWS S3, Backblaze B2, MinIO, …)</option>
                <option value="fs">
                  Folder on this machine (no cloud account, not recommended)
                </option>
              </select>

              {form.kind === "r2" && (
                <>
                  <p className="muted">
                    Paste a Cloudflare API token and we'll create a dedicated bucket and a
                    bucket-scoped credential for you — nothing to configure by hand. The token needs{" "}
                    <strong>Account &rsaquo; Workers R2 Storage &rsaquo; Edit</strong> and{" "}
                    <strong>User &rsaquo; API Tokens &rsaquo; Edit</strong> permissions. Create one
                    at{" "}
                    <a
                      href="https://dash.cloudflare.com/profile/api-tokens"
                      target="_blank"
                      rel="noreferrer"
                    >
                      dash.cloudflare.com/profile/api-tokens
                    </a>
                    . It's used once, here, and never stored.
                  </p>
                  <label>Cloudflare API token</label>
                  <input
                    type="password"
                    value={form.cloudflareApiToken}
                    onChange={(e) => set("cloudflareApiToken", e.target.value)}
                    autoComplete="off"
                  />
                  <label>Bucket name (optional — a name is generated if left blank)</label>
                  <input
                    value={form.r2BucketName}
                    onChange={(e) => set("r2BucketName", e.target.value)}
                    placeholder="tether-a1b2c3d4"
                  />
                </>
              )}

              {form.kind === "s3" && (
                <div className="grid">
                  <div>
                    <label>Bucket name</label>
                    <input value={form.bucket} onChange={(e) => set("bucket", e.target.value)} />
                    <label>Region</label>
                    <input value={form.region} onChange={(e) => set("region", e.target.value)} />
                    <label>Endpoint (leave empty for AWS)</label>
                    <input
                      value={form.endpoint}
                      onChange={(e) => set("endpoint", e.target.value)}
                      placeholder="https://…r2.cloudflarestorage.com"
                    />
                    <label className="checkbox-row" style={{ marginTop: 10 }}>
                      <input
                        type="checkbox"
                        checked={form.forcePathStyle}
                        onChange={(e) => set("forcePathStyle", e.target.checked)}
                      />
                      <span>Path-style addressing (MinIO, some providers)</span>
                    </label>
                  </div>
                  <div>
                    <label>Access key ID (leave empty to use the gateway's cloud role)</label>
                    <input
                      value={form.accessKeyId}
                      onChange={(e) => set("accessKeyId", e.target.value)}
                      autoComplete="off"
                    />
                    <label>Secret access key</label>
                    <input
                      type="password"
                      value={form.secretAccessKey}
                      onChange={(e) => set("secretAccessKey", e.target.value)}
                      autoComplete="off"
                    />
                    <label>Key prefix (optional)</label>
                    <input
                      value={form.prefix}
                      onChange={(e) => set("prefix", e.target.value)}
                      placeholder="tether/"
                    />
                  </div>
                </div>
              )}

              {form.kind === "fs" && (
                <>
                  <label>Directory</label>
                  <input value={form.dir} onChange={(e) => set("dir", e.target.value)} />
                  <div className="card warn" style={{ marginTop: 14, marginBottom: 0 }}>
                    <strong style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <AlertTriangle size={16} />
                      This is unreliable.
                    </strong>
                    <p style={{ marginTop: 6 }}>
                      Everything is stored only on this machine's disk. If it is lost, stolen, or
                      its disk fails, every recording is gone forever — there is no backup. If the
                      gateway is offline, capturing devices simply cannot upload; nothing queues
                      anywhere else. This works against the whole point of Tether for anyone relying
                      on continuous safety coverage.
                    </p>
                    <label className="checkbox-row" style={{ marginTop: 10 }}>
                      <input
                        type="checkbox"
                        checked={form.localAck}
                        onChange={(e) => set("localAck", e.target.checked)}
                      />
                      <span>
                        I understand there is no backup and nothing is stored while this gateway is
                        offline.
                      </span>
                    </label>
                  </div>
                </>
              )}

              {form.kind !== "r2" && (
                <p className="muted" style={{ marginTop: 12 }}>
                  Credentials are encrypted with the gateway secret before they are written to disk.
                </p>
              )}
              {msg && <p>{msg}</p>}
              {err && <p className="error">{err}</p>}
              <div className="row" style={{ marginTop: 12 }}>
                {form.kind === "r2" ? (
                  <button
                    className="primary"
                    onClick={provisionR2}
                    disabled={busy || !setupToken || !form.cloudflareApiToken}
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    <CloudUpload size={15} />
                    {busy ? "Creating bucket… (can take up to 20s)" : "Create bucket & connect"}
                  </button>
                ) : (
                  <>
                    <button onClick={test} disabled={busy || !setupToken}>
                      Test connection
                    </button>
                    <button
                      className="primary"
                      onClick={save}
                      disabled={busy || !setupToken || !canSaveLocal}
                    >
                      Save and continue
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="card">
          <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Fingerprint size={18} className="icon" />
            Create your passkey
          </h2>
          <p className="muted">
            Your passkey signs you in to this gateway with your fingerprint, face, or device PIN. It
            only signs you in: it does not open your recordings. That is a separate{" "}
            <strong>decryption key</strong>, which you will back up after setup.
          </p>
          <p className="muted">
            Use a passkey that syncs between your devices (iCloud Keychain, Google Password Manager,
            or a password manager). If you lose your only passkey there is no built-in recovery.{" "}
            <Link to="/guide#keys">How the keys work</Link>
          </p>
          {!passkeysSupported() && <p className="error">This browser does not support passkeys.</p>}
          {passkeyHostnameProblem() && <p className="error">{passkeyHostnameProblem()}</p>}
          <label>Your name</label>
          <input
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            placeholder="e.g. Nitya"
          />
          {err && <p className="error">{err}</p>}
          <div className="row" style={{ marginTop: 12 }}>
            <button
              className="primary"
              onClick={createOwner}
              disabled={busy || !ownerName || !setupToken}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <Fingerprint size={15} />
              {busy ? "Waiting for passkey…" : "Create passkey"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
